import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import DashboardCard from './DashboardCard';
import { capacityBand, STATUS } from '../constants';

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 7px 0;
`;

const DeptName = styled.div`
  width: 96px;
  flex-shrink: 0;
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Track = styled.div`
  position: relative;
  flex: 1;
  height: 18px;
  border-radius: 4px;
  background: #EEF1F5;
  overflow: hidden;
`;

const Fill = styled.div`
  position: absolute;
  inset: 0 auto 0 0;
  width: ${({ $pct }) => $pct}%;
  background: ${({ $color }) => $color};
  border-radius: 4px;
`;

const PctLabel = styled.span`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  font-size: 11px;
  font-weight: 700;
  ${({ $inside, $pct }) => ($inside
    ? `right: calc(${100 - $pct}% + 6px); color: #fff;`
    : `left: calc(${$pct}% + 6px); color: #52514e;`)}
`;

const People = styled.div`
  width: 84px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  font-size: 12px;
  font-weight: 600;
  color: ${({ $critical }) => ($critical ? STATUS.critical : '#52514e')};
  white-space: nowrap;
`;

const AlertBox = styled.div`
  margin-top: 14px;
  border-radius: 6px;
  background: rgba(229, 57, 53, 0.08);
  border: 1px solid rgba(229, 57, 53, 0.25);
  padding: 10px 12px;
  display: flex;
  gap: 8px;
  align-items: flex-start;
`;

const AlertText = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.primary};

  strong { color: ${STATUS.critical}; }
`;

export default function DepartmentCapacityCard({ slice }) {
  const { t } = useTranslation();
  const data = slice.data;
  const items = data?.items || [];
  const alert = data?.alert;

  return (
    <DashboardCard
      cardKey="departman-kapasitesi"
      title={t('leaveDashboard.capacity.title')}
      meta={t('leaveDashboard.capacity.meta')}
      span={6}
      spanMd={6}
      loading={slice.loading}
      empty={items.length === 0}
      domId="lvd-capacity"
    >
      <div>
        {items.map((item) => {
          const band = capacityBand(item.percent);
          const critical = band.key === 'critical';
          return (
            <Row key={item.departmentId || 'unassigned'}>
              <DeptName title={item.department || t('leaveDashboard.capacity.unassigned')}>
                {item.department || t('leaveDashboard.capacity.unassigned')}
              </DeptName>
              <Track>
                <Fill $pct={item.percent} $color={band.color} />
                <PctLabel $inside={item.percent >= 22} $pct={item.percent}>%{item.percent}</PctLabel>
              </Track>
              <People $critical={critical}>
                {t('leaveDashboard.capacity.people', { working: item.working, headcount: item.headcount })}
                {critical && <AlertTriangle size={13} />}
              </People>
            </Row>
          );
        })}
        {alert && (
          <AlertBox>
            <AlertTriangle size={16} color={STATUS.critical} />
            <AlertText>
              <strong>{t('leaveDashboard.capacity.criticalAlert', { dept: alert.department || t('leaveDashboard.capacity.unassigned') })}</strong>
              {' — '}
              {t('leaveDashboard.capacity.criticalDetail', { onLeave: alert.onLeave, headcount: alert.headcount })}
              {alert.tomorrowStarting > 0 && ` ${t('leaveDashboard.capacity.tomorrowMore', { count: alert.tomorrowStarting })}`}
            </AlertText>
          </AlertBox>
        )}
      </div>
    </DashboardCard>
  );
}
